import GenericTreeIcon from 'assets/icons/commonsIcon/generic_tree_icon.svg?react';
import EcoregionIcon_Pa12686 from 'assets/icons/commonsIcon/pa12-686_western_european_broadleaf_forests.svg';
import { getLearningPathIcon, getLearningPathIconColor } from 'components/LearningPathsIcons/LearningPathTag';
import MultilingualText from 'components/Multilingual/MultilingualText';
import IntlMessages from 'helpers/IntlMessages';
import { getPictureLocaleUrl } from 'helpers/Utils';
import ChevronLeftIcon from 'mdi-react/ChevronLeftIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import { SpecimenDetailMobileGeneralProps } from 'pages/Bioregion/Specimen//SpecimenDetailsMobileLayout';
import { getOsmTagIconAndName, OsmTagType } from 'pages/Mapping/SelectPoi';
import React, { Fragment, ReactElement, useState } from 'react';
import { Card, Row } from 'react-bootstrap';
import Lightbox from 'react-image-lightbox';
import { Link, useParams } from 'react-router-dom';

interface SpecimenOverviewProps extends SpecimenDetailMobileGeneralProps {
  setSpecimenData?: React.Dispatch<React.SetStateAction<{
    dataWtUnits: string,
    icon: ReactElement<{ height: string, className: string }>,
    titleTranslationId: string,
  }>>;
  isPrintableCard: boolean,
}

/** Content of the "Overview" tab on mobile specimen details page (the "collectible card") */
const PoiOverviewTabMobile = ({
  goBackLink,
  hasEditPermission,
  specimanBySlug,
  isPrintableCard,
  showInQuestStop,
}: SpecimenOverviewProps) => {

  const { slug, questSlug } = useParams() as any;
  const [showLightBox, setShowLightBox] = useState(false);

  // cover pic url
  const specimenPicUrl = specimanBySlug?.coverPictureUrl
    ? getPictureLocaleUrl(specimanBySlug?.coverPictureUrl)
    : specimanBySlug?.specimenAttachmentsBySpecimenId?.nodes[0]?.url;

  return (
    <Fragment>
      <Card.Body className='p-0' style={{ height: specimanBySlug.species ? 'calc(100% - 21rem)' : 'calc(100% - 16rem)' }}>
        <Row className='no-gutter m-0' style={{ maxHeight: '100%', height: '100%' }}>

          {/* specimen cover picture */}
          <div className='col-12 px-0' style={{ maxHeight: '100%', height: '100%', }}>

            {/* Buttons don't show on printable card or in quest stop complete page*/}
            {!isPrintableCard && !showInQuestStop &&
              <div
                className='d-flex justify-content-between p-2'
                style={{ position: 'absolute', top: '0', left: '0', width: '100%', zIndex: '1' }}
              >
                {/* Back button */}
                <Link
                  to={goBackLink}
                  className='btn btn-xs rounded-circle badge-outline-dark p-0'
                  style={{ aspectRatio: '1/1', backgroundColor: 'white' }}>
                  <ChevronLeftIcon size={30} color='#909090' />
                </Link>

                {/* Edit button */}
                {hasEditPermission &&
                  <Link
                    to={`/commons/${specimanBySlug.territory?.slug}/specimen/edit/${slug}`} // TODO: maybe broken for specimens without a commons
                    className='btn btn-xs rounded-circle badge-outline-orange p-0'
                    style={{ aspectRatio: '1/1', backgroundColor: '#EFB700' }}>

                    <EditOutlineIcon size={30} className='p-1' color='white' />
                  </Link>}

              </div>
            }
            {/* Cover pic */}
            <div
              className={'position-relative mb-1 card-img-top-left-top-right-rounded'}
              style={{
                overflow: 'hidden',
                maxHeight: '100%',
                height: '100%',
                backgroundImage: `url(${specimenPicUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              onClick={() => { setShowLightBox(true); }}
            />
            {(showLightBox)
              && <Lightbox
                mainSrc={specimenPicUrl}
                onCloseRequest={() => { setShowLightBox(false); }}
              />
            }
          </div>

        </Row>
      </Card.Body>

      <Card.Body className='px-0 pt-0 d-flex flex-column my-auto ' style={{ height: specimanBySlug.species ? '7rem' : '5rem' }}>

        {/* Specimen Name and Code */}
        <Row className='no-gutter m-0 py-2 my-auto'> {/* mb-auto align the row to top */}
          <div className='col-9 px-0'>
            <div className='font-weight-bold ml-3 text-truncate' style={{ maxWidth: '100%', fontSize: '1.5rem', lineHeight: '2.25rem' }}>
              <MultilingualText value={specimanBySlug.name} />
            </div>
            <div className='ml-3'>
              {getOsmTagIconAndName(specimanBySlug.osmTags as OsmTagType).iconName}
            </div>
          </div>
          <div className='col-3'>
            <div className='font-weight-light vertical-center text-center font-italic ml-n1 ' style={{ maxWidth: '100%', fontSize: '1rem', fontWeight: '100' }}>
              {specimanBySlug.code}
            </div>
          </div>
        </Row>

        {(specimanBySlug.type === 'POI' && specimanBySlug.species) &&
          <>
            <div className='border-bottom my-auto' /> {/* my-auto align the div in center */}

            {/* this is just copied from the original specimen card and might not be necessary at all */}
            {!isPrintableCard
              ? <Link
                className='d-flex flex-row flex-noWrap align-items-center py-2 w-100 my-auto' //* my-auto align the div in center */}
                to={
                  {
                    pathname: specimanBySlug.species ? `/species/${specimanBySlug.species.slug}` : `/commons/${specimanBySlug.territory.slug}/specimen/edit/${slug}`,
                    state: showInQuestStop && { breadcrumbsTranslationId: 'route.quest', from: `/quest/${questSlug}/map` }
                  }
                }
              >
                <div className='mr-auto d-flex ml-3 flex-fill' style={{ minWidth: '0' }}> {/* if an element styled with 'text-truncate' is not direct child of a flex element, then its parent component must have min-width = 0 or some actual value */}
                  <GenericTreeIcon height={'2.25rem'} className={'my-auto'} />
                  <div className='d-flex flex-column mr-auto ml-3 my-auto' style={{ minWidth: '0' }}> {/* if an element styled with 'text-truncate' is not direct child of a flex element, then its parent component must have min-width = 0 or some actual value */}
                    {specimanBySlug.species
                      ? <Fragment>
                        <div className='h4 mb-0 color-theme-1 text-truncate' style={{ lineHeight: '1.5rem', maxWidth: '100%' }}>
                          <MultilingualText value={specimanBySlug.species.name} />
                        </div>
                        <div className='color-theme-1 text-truncate font-italic ' style={{ lineHeight: '1.25rem', maxWidth: '100%' }}>
                          {specimanBySlug.species.scientificName}&nbsp;
                        </div>
                      </Fragment>
                      : <IntlMessages id={'specimen.unknown-species'} />
                    }
                  </div>
                </div>
                <ChevronRightIcon size={'40'} color='#60b6aa' className='flex-fill' style={{ maxWidth: '25%' }} />
              </Link>

              : <Row className='no-gutters my-auto'>
                <div className='col-9 d-flex flex-column my-auto pl-3' style={{ minWidth: '0' }}> {/* if an element styled with 'text-truncate' is not direct child of a flex element, then its parent component must have min-width = 0 or some actual value */}
                  {specimanBySlug.species
                    ? <Fragment>
                      <div className='h4 mb-0 color-theme-1 text-truncate' style={{ lineHeight: '1.5rem', maxWidth: '100%' }}>
                        <MultilingualText value={specimanBySlug.species.name} />
                      </div>
                      <div className='color-theme-1 text-truncate font-italic ' style={{ lineHeight: '1.25rem', maxWidth: '100%' }}>
                        {specimanBySlug.species.scientificName}&nbsp;
                      </div>
                    </Fragment>
                    : <IntlMessages id={'specimen.unknown-species'} />
                  }
                </div>
                <div className='col-3 d-flex'>
                  <GenericTreeIcon height={'2.25rem'} className='my-auto mx-auto' />
                </div>
              </Row>
            }

            <div className='border-bottom my-auto' /> {/* my-auto align the div in center */}

          </>
        }

        <div
          className='d-flex flex-row flex-nowrap justify-content-center align-items-center pt-1 my-auto' //* my-auto align the div in center */
          style={{ height: '100px' }} /* pt-2 = 0.5rem, pb-1=0.25rem, 2.25rem is the lineHeight of the specimen name, 2.75rem is the lineHeight of the species info*/
        >

          {/* commons avatar */}
          <div className='flex-1 d-flex justify-content-center'>
            <div style={{ aspectRatio: '1/1' }} className='my-auto py-1 d-flex'>
              <img
                src={specimanBySlug.territory?.avatarUrl ?? '/assets/img/profiles/l-1.jpg'}
                alt={specimanBySlug.territory?.name?.en}
                className='rounded-circle mx-auto'
                style={{ objectFit: 'cover', height: '60px', maxWidth: '100%', }}
              />
            </div>
          </div>

          {/* ecoregion avatar */}
          <div className='flex-1 mx-3 d-flex justify-content-center'>
            <img
              src={EcoregionIcon_Pa12686}
              alt='ecoregion avatar'
              className='rounded-circle py-1 my-auto'
              style={{ height: '90px', aspectRatio: '1/1' }}
            />
          </div>

          <div className='flex-1 d-flex justify-content-center'>
            <div style={{ height: '50px', aspectRatio: '1/1' }} className='my-auto py-1 d-flex'>
              {getLearningPathIcon(specimanBySlug.learningPath?.id, getLearningPathIconColor(specimanBySlug.learningPath?.id), 50)}
            </div>
          </div>
        </div>

      </Card.Body>
    </Fragment>
  );
};

export default PoiOverviewTabMobile;
